import { FilterBarPage, FilterBarState, FilterBarStateKey, SelectOption } from '.';

import i18n from '@/i18n';
import { makeUnique } from '@/services/utils';
import { RootState } from '@/store';

function translatedSortOptions(i18n: any) {
  return [
    { value: 'recommended', label: i18n.global.t('@@translate@@ Default') },
    { value: 'alphabetical', label: i18n.global.t('@@translate@@ A-Z') },
    { value: 'random', label: i18n.global.t('@@translate@@ Random') }
  ];
}
function getPages({ count, perPage }: FilterBarPage) {
  return count ? Math.ceil(count / perPage) : 1;
}

const getters = {
  EventBoothsGridPage2(state: FilterBarState, getters: any, rootState: RootState) {
    return {
      ...state.EventBoothsGridPage2,
      tags: getters.topTags('EventBoothsGridPage'),
      sortOptions: translatedSortOptions(i18n),
      pages: getPages(state.EventBoothsGridPage2)
    };
  },
  EventBoothsGridPage(state: FilterBarState, getters: any, rootState: RootState) {
    return {
      ...state.EventBoothsGridPage,
      tags: getters.topTags('EventBoothsGridPage'),
      sortOptions: translatedSortOptions(i18n),
      pages: getPages(state.EventBoothsGridPage)
    };
  },
  DiscussionGroups(state: FilterBarState, getters: any, rootState: RootState) {
    return {
      ...state.DiscussionGroups,
      tags: getters.topTags('DiscussionGroups'),
      sortOptions: translatedSortOptions(i18n),
      pages: getPages(state.DiscussionGroups)
    };
  },
  EventNetwork(state: FilterBarState, getters: any, rootState: RootState) {
    return {
      ...state.EventNetwork,
      tags: getters.topTags('EventNetwork'),
      sortOptions: translatedSortOptions(i18n),
      pages: getPages(state.EventNetwork)
    };
  },
  EventAgendaPage(state: FilterBarState, getters: any, rootState: RootState, allGetters: { [k: string]: any }) {
    const { dates } = allGetters['event/agendaDateOptions'];
    const tracks = allGetters['event/tracks'];
    return {
      ...state.EventAgendaPage,
      tags: getters.topTags('EventAgendaPage'),
      sortOptions: [
        {
          label: i18n.global.t('@@translate@@ Open'),
          value: 'open'
        },
        {
          label: i18n.global.t('@@translate@@ All'),
          value: 'all'
        },
        {
          label: i18n.global.t('@@translate@@ Past'),
          value: 'past'
        },
        {
          label: i18n.global.t('@@translate@@ Upcoming'),
          value: 'upcoming'
        }
      ],
      dates,
      dateFilter: (state.EventAgendaPage.dateFilter.value && state.EventAgendaPage.dateFilter) || dates[0],
      tracks,
      trackFilter:
        (state.EventAgendaPage.trackFilter.value && state.EventAgendaPage.trackFilter) || allGetters['event/userTrack']
    };
  },

  StageScreenSaver(state: FilterBarState, getters: any, rootState: RootState, rootGetters: { [k: string]: any }) {
    const { dates } = rootGetters['event/agendaDateOptions'];
    const tracks = rootGetters['event/tracks'];
    return {
      ...state.StageScreenSaver,
      dates,
      dateFilter: (state.StageScreenSaver.dateFilter.value && state.StageScreenSaver.dateFilter) || dates[0],
      tracks,
      trackFilter: state.StageScreenSaver.trackFilter
    };
  },

  filters: (state: any, getters?: any) => (page: string) => {
    return getters[page];
  },
  getPaginationParams: (state: FilterBarState, getters: any, rootState: RootState) => (page: FilterBarStateKey) => {
    let { perPage: limit } = state[page];
    const { currentPage } = state[page];
    if (rootState.route.query.limit) {
      limit = +rootState.route.query.limit;
    }
    let offset = (currentPage - 1) * limit;
    if (rootState.route.query.offset) {
      offset = +rootState.route.query.offset;
    }
    return {
      limit,
      offset
    };
  },
  getQueryParams:
    (state: FilterBarState, getters: any, rootState: RootState, rootGetters: { [k: string]: any }) =>
    (page: FilterBarStateKey) => {
      const { searchTerm: search, filterTags: tags = [], sortBy } = state[page];
      return {
        search,
        tags,
        ...sortValue(sortBy)
      };

      function sortValue({ value }: SelectOption) {
        if (value === 'alphabetical') {
          const options: any = {
            DiscussionGroups: 'title',
            EventNetwork: 'user__first_name',
            EventBoothsGridPage: 'name',
            EventBoothsGridPage2: 'name'
          };
          return {
            ordering: options[page]
          };
        }
        if (value === 'recommended') {
          return {
            recommended: (rootGetters.getCurrentUser?.tags || []).join(',')
          };
        }
        if (value === 'random') {
          const options: any = {};
          return {
            ordering: options[page]
          };
        }
        return {};
      }
    },
  dataLoadRequestParams: (state: FilterBarState, getters: any) => (page: FilterBarStateKey) => {
    const routes = page.split('-');
    //@gc-todo: remove this hack
    const newPage = routes.length >= 2 ? routes[1] : routes[0];
    return {
      ...getters.getPaginationParams(newPage),
      ...getters.getQueryParams(newPage)
    };
  },
  topTags:
    (state: FilterBarState, getters: any, rootState: RootState, rootGetters: { [k: string]: any }) =>
    (page: string) => {
      const pages: any = {
        EventNetwork: 'EventUsers',
        DiscussionGroups: 'DiscussionGroup',
        EventAgendaPage: 'Broadcast',
        EventBoothsGridPage: 'Booth'
      };
      const meta_tags = rootGetters['event/currentEvent'].meta_tag || {};

      const tags = meta_tags[pages[page]] || {};

      return Object.keys(tags).sort((a, b) => tags[b] - tags[a]);
    },
  allTopTags(state: FilterBarState, getters: any) {
    const tags = [
      ...getters.EventAgendaPage.tags,
      ...getters.EventNetwork.tags,
      ...getters.DiscussionGroups.tags,
      ...getters.EventBoothsGridPage.tags
    ];

    return makeUnique<string>(tags).slice(0, 5);
  },
  allPropposedTags() {
    return ['tech', 'science', 'business', 'arts'];
  },
  currentPageName(state: FilterBarState, getters: any, rootState: RootState) {
    //@gc-todo: remove this hack
    const routes = rootState.route?.name?.toString().split('-');
    let newPage = rootState.route?.name;
    if (routes) {
      newPage = routes.length >= 2 ? routes[1] : routes[0];
    }
    return rootState.route.name === 'EventSplash' ? 'EventAgendaPage' : newPage;
  }
};

export default getters;
export type FilterBarGetters = typeof getters;
